import { action, computed, makeObservable, observable } from 'mobx'

import {
  DeliveryFilterType,
  IColumnConfiguration,
  IWorkflowConfigurations,
  SitePermitStatus,
} from '~/client/graph'
import LogisticActionTypes from '~/client/src/desktop/enums/LogisticsActionTypes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import FileTypeIcon from '~/client/src/desktop/views/Attachments/components/AttachmentsList/FileTypeIcon'
import {
  ILWFCCategory,
  ILWFCColumn,
  ILWFCRow,
  LWFCRowData,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import LogisticsGroupingOption from '~/client/src/shared/enums/LogisticsGroupingOption'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ILogisticItem, {
  ILogisticItemWorkflow,
} from '~/client/src/shared/models/ILogisticItem'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import { getSharedProjectName } from '~/client/src/shared/models/SharedInfo'
import User from '~/client/src/shared/models/User'
import Workflow, {
  workflowDoneStatuses,
} from '~/client/src/shared/models/Workflow'
import BaseLogisticsList from '~/client/src/shared/stores/BaseLogisticsLists.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import SitePermitFollowingsStore from '~/client/src/shared/stores/domain/SitePermitFollowings.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import {
  LOAD_AND_LISTEN_TO_WORKFLOWS_CONFIGURATIONS,
  SAVE_WORKFLOWS_CONFIGURATIONS,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import {
  CATEGORY_ROW_HEIGHT,
  DEFAULT_ID_KEY,
  ITreeNodeObj,
} from '~/client/src/shared/stores/ui/BaseList.store'
import BaseMultiBandListStore from '~/client/src/shared/stores/ui/BaseMultiBandList.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { LogisticsDataKeys } from '~/client/src/shared/types/LogisticsDataKeys'
import {
  createFilterValueByType,
  getSelectedFilterValues,
  extractWorkflowPermits,
  isLogisticWorkflow,
  typeNameToSortIndexMap,
} from '~/client/src/shared/utils/logisticsHelper'
import {
  groupingAttributeMapper,
  groupingCommonMapper,
} from '~/client/src/shared/utils/mappers'
import {
  getCommonStepItems,
  IStatusItem,
  resolveStepForStatus,
} from '~/client/src/shared/utils/permitStatusUtils'
import {
  EMPTY_STRING,
  LOCATION_SEPARATOR,
  NO_SPECIFIED,
  NO_VALUE,
} from '~/client/src/shared/utils/usefulStrings'
import { getWorkflowGroupingVisualData } from '~/client/src/shared/utils/workflowGroupingVisualData'
import { advanceToNextWorkflowStep } from '~/client/src/shared/utils/workflowStepUtils'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import LogisticsStore from '../Logistics/Logistics.store'

const unassignedValues = [UNASSIGNED, NO_SPECIFIED, EMPTY_STRING, NO_VALUE]
const SITE_COMMUNICATION_CATEGORY = 'Site Communication'

export default class LogisticsListStore
  extends BaseMultiBandListStore<ILogisticItem>
  implements BaseLogisticsList
{
  protected readonly collator = new Intl.Collator([], {
    numeric: true,
    sensitivity: 'accent',
  })
  @observable public shouldSubscriptionConfirmModalShow: boolean = false
  @observable public isDeletionConfirmModalShown: boolean = false
  @observable public isBatchStatusUpdateConfirmModalShown: boolean = false
  @observable public selectedStatusForBatchUpdate: IStatusItem = null

  public constructor(
    protected readonly logisticsStore: LogisticsStore,
    protected readonly projectDateStore: ProjectDateStore,
    private readonly workflowsStore: WorkflowsStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    protected readonly companiesStore: CompaniesStore,
    protected readonly projectMembersStore: ProjectMembersStore,
    protected readonly userProjectsStore: UserProjectsStore,
    private readonly projectsStore: ProjectsStore,
    protected readonly state: DesktopInitialState,
    private readonly eventsStore: DesktopEventStore,
    private readonly workflowFollowingsStore?: SitePermitFollowingsStore,
    private readonly workflowTypesStore?: WorkflowTypesStore,
    protected readonly workflowCategoriesStore?: WorkflowCategoriesStore,
    private readonly sharedSitePermitStore?: SharedSitePermitStore,
  ) {
    super(
      logisticsStore.filters,
      () => logisticsStore.allLogistics,
      [],
      DEFAULT_ID_KEY,
    )
    makeObservable(this)
  }

  @observable
  public columnsWidthState = new Map<string, number>([
    [LogisticsDataKeys.CHECKBOX, 40],
    [LogisticsDataKeys.LOGISTIC_ID, 250],
    [LogisticsDataKeys.LOGISTIC_NAME, 250],
    [LogisticsDataKeys.LOGISTIC_TYPE, 200],
    [LogisticsDataKeys.LOGISTIC_CATEGORY, 200],
    [LogisticsDataKeys.LOGISTIC_STATUS, 190],
    [LogisticsDataKeys.LOGISTIC_LOCATION, 300],
    [LogisticsDataKeys.LOGISTIC_EQUIPMENT, 300],
    [LogisticsDataKeys.LOGISTIC_COMPANY, 230],
    [LogisticsDataKeys.LOGISTIC_DATE, 250],
    [LogisticsDataKeys.LOGISTIC_RESPONSIBLE, 300],
    [LogisticsDataKeys.LOGISTIC_ATTACHMENT, 200],
  ])

  @computed
  public get defaultColumns(): ILWFCColumn[] {
    const columns: ILWFCColumn[] = [
      {
        dataKey: LogisticsDataKeys.CHECKBOX,
      },
      {
        label: Localization.translator.id_short,
        dataKey: LogisticsDataKeys.LOGISTIC_ID,
        flexGrow: 1,
        isFixed: true,
      },
      {
        label: Localization.translator.name,
        dataKey: LogisticsDataKeys.LOGISTIC_NAME,
        isFixed: true,
      },
      {
        label: Localization.translator.date,
        dataKey: LogisticsDataKeys.LOGISTIC_DATE,
        isFixed: true,
      },
      {
        label: Localization.translator.type,
        dataKey: LogisticsDataKeys.LOGISTIC_TYPE,
      },
      {
        label: Localization.translator.category,
        dataKey: LogisticsDataKeys.LOGISTIC_CATEGORY,
      },
      {
        label: Localization.translator.status,
        dataKey: LogisticsDataKeys.LOGISTIC_STATUS,
      },
      {
        label: Localization.translator.location,
        dataKey: LogisticsDataKeys.LOGISTIC_LOCATION,
      },
      {
        label: Localization.translator.equipment,
        dataKey: LogisticsDataKeys.LOGISTIC_EQUIPMENT,
      },
      {
        label: Localization.translator.company,
        dataKey: LogisticsDataKeys.LOGISTIC_COMPANY,
      },
      {
        label: Localization.translator.responsibleUser,
        dataKey: LogisticsDataKeys.LOGISTIC_RESPONSIBLE,
      },
      {
        label: Localization.translator.file,
        dataKey: LogisticsDataKeys.LOGISTIC_ATTACHMENT,
      },
    ]
    return columns.map(column => ({
      ...column,
      width: this.columnsWidthState.get(column.dataKey),
      isHidden: false,
      isFixed:
        column.dataKey === LogisticsDataKeys.CHECKBOX || !!column.isFixed,
    }))
  }

  @computed
  public get columns(): ILWFCColumn[] {
    const columnsConfig =
      this.workflowsConfiguration?.[this.configFieldName] || []

    if (!columnsConfig?.length) {
      return this.defaultColumns
    }

    const mappedConfigCols = columnsConfig.reduce((list, columnSorted) => {
      const column = this.defaultColumns.find(
        _ => _.dataKey == columnSorted.columnId,
      )
      if (column) {
        list.push({
          ...column,
          width: this.columnsWidthState.get(column.dataKey),
          isHidden: column.isHidden || columnSorted.isHidden,
          isFixed: columnSorted.isFixed,
        })
      }
      return list
    }, [] as ILWFCColumn[])

    const absentColumns = this.defaultColumns.filter(
      col => !mappedConfigCols.some(config => config.dataKey === col.dataKey),
    )
    absentColumns.forEach(col =>
      mappedConfigCols.push({ ...col, isFixed: false }),
    )

    return mappedConfigCols
  }

  @computed
  public get fixedColumnsCount() {
    return this.columns.filter(c => c.isFixed).length
  }

  protected get workflowsConfiguration(): IWorkflowConfigurations {
    return this.state.workflows.configurations
  }

  protected get configFieldName(): 'listColumnsConfig' | 'ganttColumnsConfig' {
    return 'listColumnsConfig'
  }

  public get isConfigurationLoading(): boolean {
    return (
      this.state.loading.get(LOAD_AND_LISTEN_TO_WORKFLOWS_CONFIGURATIONS) ||
      this.state.loading.get(SAVE_WORKFLOWS_CONFIGURATIONS)
    )
  }

  @action.bound
  public resetColumnsConfiguration(): void {
    this.workflowsConfiguration[this.configFieldName] = this.mapColumnsToConfig(
      this.workflowsConfiguration?.[this.configFieldName] ||
        this.defaultColumnsConfig,
      this.defaultColumns,
    )

    this.saveWorkflowsColumnConfiguration()
  }

  @action.bound
  public updateColumnsConfiguration(collection: ILWFCColumn[]): void {
    this.workflowsConfiguration[this.configFieldName] = this.mapColumnsToConfig(
      this.workflowsConfiguration?.[this.configFieldName] ||
        this.defaultColumnsConfig,
      collection,
    )

    this.saveWorkflowsColumnConfiguration()
  }

  private mapColumnsToConfig(
    columnsConfig: IColumnConfiguration[],
    collection: ILWFCColumn[],
  ): IColumnConfiguration[] {
    const nonPresentConfigs = columnsConfig.filter(
      c => !collection.some(col => col.dataKey === c.columnId),
    )
    const presentConfigs = collection.map(column => ({
      columnId: column.dataKey,
      isHidden: column.isHidden || false,
      isFixed: column.isFixed || false,
    }))
    return [...presentConfigs, ...nonPresentConfigs]
  }

  private get defaultColumnsConfig(): IColumnConfiguration[] {
    return this.defaultColumns.map(column => ({
      columnId: column.dataKey,
      isHidden: column.isHidden || false,
      isFixed: column.isFixed || false,
    }))
  }

  @action.bound
  private async saveWorkflowsColumnConfiguration() {
    this.eventsStore.dispatch(
      SAVE_WORKFLOWS_CONFIGURATIONS,
      this.workflowsConfiguration,
    )
  }

  @computed
  public get statusTitle(): string {
    if (this.isSomeRowSelected) {
      return `${this.selection.size} of ${this.displayedCount} selected`
    }

    return Localization.translator.xWorkflowsShowing(this.displayedCount)
  }

  public get rows(): ILWFCRow[] {
    const { groupingKey } = this.logisticsStore.filters

    return this.toBandTreeNodeRows(
      [groupingKey],
      null,
      '',
      0,
      false,
      this.sortTreeObjs,
    )
  }

  private sortTreeObjs = (treeNodeObjs: ITreeNodeObj[]) => {
    const map = new Map<string, ITreeNodeObj>()

    treeNodeObjs.forEach(obj => map.set(obj.id, obj))

    return Array.from(map.values()).sort((a, b) => {
      if (unassignedValues.includes(a.id)) {
        return 1
      }
      if (unassignedValues.includes(b.id)) {
        return -1
      }
      if (this.groupingKey === LogisticsGroupingOption.CATEGORY) {
        const target = SITE_COMMUNICATION_CATEGORY
        const aIsTarget = (a.name || '').toLowerCase() === target.toLowerCase()
        const bIsTarget = (b.name || '').toLowerCase() === target.toLowerCase()
        if (aIsTarget && !bIsTarget) return -1
        if (bIsTarget && !aIsTarget) return 1
      }
      if (this.groupingKey === LogisticsGroupingOption.TYPE) {
        const aIndex =
          typeNameToSortIndexMap[a.name] ?? Number.POSITIVE_INFINITY
        const bIndex =
          typeNameToSortIndexMap[b.name] ?? Number.POSITIVE_INFINITY
        if (aIndex !== bIndex) return aIndex - bIndex
      }

      if (
        this.groupingKey === LogisticsGroupingOption.DATE ||
        this.groupingKey === LogisticsGroupingOption.DATE_TIME
      ) {
        return parseInt(a.id, 10) - parseInt(b.id, 10)
      }

      return this.collator.compare(a.name, b.name)
    })
  }

  @computed
  public get categoryToInstancesMap(): {
    [categoryKey: string]: ILogisticItem[]
  } {
    const map = {}

    this.filteredCollection.forEach(logistic => {
      const categoryIds = this.getCategoryIds(logistic, this.groupingKey)
      categoryIds.forEach(formattedCategoryId => {
        if (map[formattedCategoryId]) {
          map[formattedCategoryId].push(logistic)
        } else {
          map[formattedCategoryId] = [logistic]
        }
      })
    })

    return map
  }

  private get filterValueByType() {
    return createFilterValueByType(
      this.logisticsStore.getSharedFormById,
      () => this.state.activeProject.id,
    )
  }

  public getFilteredCollectionExcludeFilter = (
    excludedFilters: Array<DeliveryFilterType | string> = [],
  ) => {
    let filteredCollection = this.isSearchFilterActive
      ? this.instancesInPeriodInterval.filter(this.searchFiltering)
      : this.instancesInPeriodInterval

    this.activeFilterTypes.forEach(filterType => {
      const shouldUseFilter = !excludedFilters.includes(filterType)

      if (!shouldUseFilter) {
        return
      }

      const filter = this.filter.fieldsMap[filterType]
      const { initialFilterOptions } = filter
      const selectedValues = getSelectedFilterValues(
        filterType,
        initialFilterOptions,
      )

      filteredCollection = filteredCollection.filter(inst => {
        const valueGetter = this.filterValueByType[filterType]
        const value = valueGetter(inst)

        if (initialFilterOptions.has(UNASSIGNED) && !value.length) {
          return true
        }

        return value.some(v => selectedValues.has(v))
      })
    })

    return filteredCollection
  }

  private getCategoryIds(logistic: ILogisticItem, band: string) {
    const { startOfDay } = this.projectDateStore

    switch (band) {
      case LogisticsGroupingOption.COMPANY:
        return logistic.companyIds?.map(
          id => this.companiesStore.getCompanyById(id)?.id || UNASSIGNED,
        )
      case LogisticsGroupingOption.RESPONSIBLE:
        const members = this.projectMembersStore.getByIds(
          logistic.responsibleContactIds,
        )
        return members.map(member => member.id || UNASSIGNED)
      case LogisticsGroupingOption.DATE:
        return [
          startOfDay(logistic.startDate).getTime().toString() || UNASSIGNED,
        ]
      case LogisticsGroupingOption.DATE_TIME:
        return [logistic.startDate?.toString() || UNASSIGNED]
      case LogisticsGroupingOption.STATUS:
        return [logistic.status || UNASSIGNED]
      case LogisticsGroupingOption.TYPE:
        return [logistic.type || UNASSIGNED]
      case LogisticsGroupingOption.FILE_TYPE:
        return Array.from(
          new Set(
            (logistic.attachments || []).map(a =>
              getAttachmentFileType(a.fileName, '.', true),
            ),
          ),
        )
      case LogisticsGroupingOption.PROJECT:
        const sharedEntity = this.logisticsStore.getSharedFormById(logistic.id)
        return [
          sharedEntity?.projectFromId ||
            this.state.activeProject.id ||
            UNASSIGNED,
        ]
      case LogisticsGroupingOption.LBS:
        return this.getLocationCategoryIds(logistic.locations)
      case LogisticsGroupingOption.EQUIPMENT:
        return this.getLocationCategoryIds(logistic.equipment)
      case LogisticsGroupingOption.NONE:
        return [LogisticsGroupingOption.NONE]
      case LogisticsGroupingOption.CATEGORY:
        return [logistic.formCategoryId || UNASSIGNED]
      default:
        return [UNASSIGNED]
    }
  }

  @action.bound
  public selectRow(objectId: string) {
    this.resetSelection()
    this.selection.set(objectId, true)
  }

  @action.bound
  public onRowClick(rowData: LWFCRowData, columnKey: string) {
    if (columnKey === LogisticsDataKeys.CHECKBOX) {
      this.toggleRowCheckbox(rowData[LogisticsDataKeys.ID])
      return
    }

    const workflowId = rowData[LogisticsDataKeys.ID]

    const logistic = this.filteredCollection.find(
      logistic => logistic.id === workflowId,
    )

    if (isLogisticWorkflow(logistic)) {
      const permit = logistic.original

      if (permit) {
        this.logisticsStore.showWorkflowViewDialog(permit, () => {
          this.setInstanceSelection(workflowId, false)
        })
      }
      const sharedPermit = this.sharedSitePermitStore.getById(logistic.entityId)
      if (sharedPermit) {
        this.logisticsStore.setSelectedSharedEntity(sharedPermit)
      }
    }

    this.setInstanceSelection(workflowId, true)
  }

  public toggleRowCheckbox = (objectId: string) => {
    this.toggleInstance(objectId)
  }

  @action.bound
  public setSelectedIds(ids: string[]) {
    this.selection.clear()
    ids.forEach(id => this.selection.set(id, true))
  }

  public toggleListCategory = (category: ILWFCCategory) => {
    this.toggleCategory(category)
  }

  @action.bound
  public setActionType(actionType: LogisticActionTypes) {
    if (actionType === LogisticActionTypes.SUBSCRIBE) {
      return this.openSubscriptionConfirmModal()
    }
    if (actionType === LogisticActionTypes.DELETE) {
      return this.openDeletionConfirmModal()
    }
    if (actionType === LogisticActionTypes.DUPLICATE) {
      if (this.selectedInstancesIds.length !== 1) return
      this.duplicateWorkflow(this.selectedInstancesIds[0])
      return
    }
  }

  public duplicateWorkflow(id: string) {
    const logistic = this.filteredCollection.find(l => l.id === id)
    if (!logistic || !isLogisticWorkflow(logistic)) return
    this.logisticsStore.startDuplicateWorkflow(logistic.original)
  }

  @action.bound
  public openSubscriptionConfirmModal() {
    this.shouldSubscriptionConfirmModalShow = true
  }

  @action.bound
  public closeSubscriptionConfirmModal() {
    this.shouldSubscriptionConfirmModalShow = false
  }

  @action.bound
  public openBatchStatusUpdateConfirmModal(item: IStatusItem) {
    this.selectedStatusForBatchUpdate = item
    this.isBatchStatusUpdateConfirmModalShown = true
  }

  @action.bound
  public closeBatchStatusUpdateConfirmModal() {
    this.selectedStatusForBatchUpdate = null
    this.isBatchStatusUpdateConfirmModalShown = false
  }

  @action.bound
  public openDeletionConfirmModal() {
    this.isDeletionConfirmModalShown = true
  }

  @action.bound
  public closeDeletionConfirmModal() {
    this.isDeletionConfirmModalShown = false
  }

  @computed
  public get unsubscribedSelectedLogisticsIds(): string[] {
    if (!this.selectedInstances.length) {
      return []
    }
    return this.selectedInstances
      .filter(l => !this.workflowFollowingsStore.isEntityFollowed(l.id))
      .map(l => l.id)
  }

  @computed
  public get deletionConfirmMessage(): string {
    return Localization.translator.logisticsListDescriptions.deletionConfirmMsg(
      this.selectedInstancesCount,
    )
  }

  @computed
  public get subscriptionConfirmMessage(): string {
    const dSelectedCount = this.selection.size
    const dUnsubscribedCount = this.unsubscribedSelectedLogisticsIds.length

    const { subscribeConfirmMassage, unsubscribeConfirmMessage } =
      Localization.translator.logisticsListDescriptions

    if (this.isSubscribeMode) {
      return subscribeConfirmMassage(dUnsubscribedCount, dSelectedCount)
    }

    return unsubscribeConfirmMessage(dSelectedCount)
  }

  @computed
  public get subscriptionConfirmButtonText(): string {
    return this.isSubscribeMode
      ? Localization.translator.subscribe_verb
      : Localization.translator.yesUnsubscribe
  }

  public get isSubscribeMode(): boolean {
    return !!this.unsubscribedSelectedLogisticsIds.length
  }

  @action.bound
  public async deleteWorkflows() {
    if (this.isUpdating || this.areSelectedWorkflowsDone) {
      return
    }

    await this.workflowsStore.deleteWorkflows(
      this.selectedInstances.filter(isLogisticWorkflow).map(i => i.original),
    )

    this.closeDeletionConfirmModal()
    this.resetSelection()
  }

  @action.bound
  public handleChangeAssociations() {
    if (this.areSelectedWorkflowsDraft) {
      return
    }

    if (this.isSubscribeMode) {
      this.subscribe()
    } else {
      this.unsubscribe()
    }

    this.closeSubscriptionConfirmModal()
  }

  public get areSelectedWorkflowsDone(): boolean {
    return this.selectedInstances.some(i =>
      workflowDoneStatuses.includes(i.status as SitePermitStatus),
    )
  }

  public get areSelectedWorkflowsDraft(): boolean {
    return this.selectedInstances.some(i => i.status === SitePermitStatus.Draft)
  }

  public get isUpdating(): boolean {
    return this.workflowsStore.isDeleting
  }

  @action.bound
  private subscribe() {
    const entityIds = this.selectedInstances
      .filter(l => this.unsubscribedSelectedLogisticsIds.includes(l.id))
      .map(l => l.entityId)

    if (entityIds.length) {
      this.workflowFollowingsStore.followEntities(entityIds)
    }
  }

  @action.bound
  private unsubscribe() {
    const ids = this.selectedInstances.map(l => l.id)

    if (ids.length) {
      this.workflowFollowingsStore.unfollowEntities(ids)
    }
  }

  @computed
  public get workflowPermits(): Workflow[] {
    return extractWorkflowPermits(this.filteredCollection)
  }

  protected getTreeNodeObjsByBand(currentBand: string): ITreeNodeObj[] {
    const {
      getMonthDayYearAndTimeToDisplay,
      getMonthDayAndYearToDisplay,
      startOfDay,
    } = this.projectDateStore

    switch (currentBand) {
      case LogisticsGroupingOption.NONE:
        return [
          {
            id: LogisticsGroupingOption.NONE,
            name: LogisticsGroupingOption.NONE,
          },
        ]
      case LogisticsGroupingOption.COMPANY:
        return [
          ...this.filteredCollection
            .flatMap(logistic => logistic.companyIds)
            .map(companyId => {
              const company = this.companiesStore.getCompanyById(companyId)
              return {
                name: company?.name || UNASSIGNED,
                id: company?.id || UNASSIGNED,
                object: this.getCommonGroupingData(
                  currentBand,
                  company?.id || UNASSIGNED,
                ),
              }
            }),
          groupingCommonMapper(UNASSIGNED),
        ]
      case LogisticsGroupingOption.RESPONSIBLE:
        return [
          ...this.filteredCollection
            .flatMap(logistic => logistic.responsibleContactIds)
            .map(userId => {
              const user = this.projectMembersStore.getById(userId)
              return {
                name: user?.fullName || UNASSIGNED,
                id: user?.id || UNASSIGNED,
                object: this.getCommonGroupingData(
                  currentBand,
                  user?.id || UNASSIGNED,
                ),
              }
            }),
          groupingCommonMapper(UNASSIGNED),
        ]
      case LogisticsGroupingOption.DATE:
        return this.filteredCollection
          .map(logistic => logistic.startDate)
          .map(date => {
            return {
              name: getMonthDayAndYearToDisplay(date) || UNASSIGNED,
              id: startOfDay(date).getTime().toString() || UNASSIGNED,
            }
          })
      case LogisticsGroupingOption.DATE_TIME:
        return this.filteredCollection
          .map(logistic => logistic.startDate)
          .map(date => {
            return {
              name: getMonthDayYearAndTimeToDisplay(date) || UNASSIGNED,
              id: date?.toString() || UNASSIGNED,
            }
          })
      case LogisticsGroupingOption.LBS:
        return [
          ...this.locationAttributesStore.attributesWithDeletedWithoutEquipment.map(
            groupingAttributeMapper,
          ),
          groupingCommonMapper(UNASSIGNED),
        ]
      case LogisticsGroupingOption.EQUIPMENT:
        return [
          ...this.locationAttributesStore.offloadingEquipmentsStore.listWithDeletedItems.map(
            groupingAttributeMapper,
          ),
          groupingCommonMapper(UNASSIGNED),
        ]

      case LogisticsGroupingOption.STATUS:
        return [
          ...Object.values(SitePermitStatus).map(s => ({
            id: s,
            name: getFormStatusDisplayName(s),
            object: this.getCommonGroupingData(currentBand, s),
          })),
        ]
      case LogisticsGroupingOption.TYPE:
        return [
          ...this.filteredCollection
            .flatMap(logistic => logistic.type)
            .map(type => {
              const permitType =
                this.workflowTypesStore.getLastUpdatedTypeByType(type)
              return {
                name:
                  this.logisticsStore.getFormNameToDisplay(permitType) ||
                  UNASSIGNED,
                id: type || UNASSIGNED,
                object: this.getCommonGroupingData(
                  currentBand,
                  type || UNASSIGNED,
                ),
              }
            }),
          groupingCommonMapper(UNASSIGNED),
        ]
      case LogisticsGroupingOption.FILE_TYPE:
        return [
          ...this.filteredCollection
            .flatMap(logistic => logistic.attachments || [])
            .map(a => getAttachmentFileType(a.fileName, '.', true))
            .map(fileType => ({
              id: fileType || UNASSIGNED,
              name: (fileType || UNASSIGNED).toUpperCase(),
              object: {
                icon: FileTypeIcon,
                iconProps: { fileType },
              },
            })),
          groupingCommonMapper(UNASSIGNED),
        ]

      case LogisticsGroupingOption.PROJECT:
        const sharedProjectsIds = this.filteredCollection.reduce(
          (acc, logistic) => {
            const sharedEntity = this.logisticsStore.getSharedFormById(
              logistic.id,
            )
            acc[sharedEntity?.projectFromId || this.state.activeProject.id] =
              sharedEntity?.projectUrl
            return acc
          },
          {},
        )
        const sharedProjects = Object.keys(sharedProjectsIds).map(
          projectId => ({
            name:
              getSharedProjectName(
                sharedProjectsIds[projectId],
                this.projectsStore,
                projectId,
              ) || UNASSIGNED,
            id: projectId || UNASSIGNED,
          }),
        )
        return [...sharedProjects]

      case LogisticsGroupingOption.CATEGORY:
        return [
          ...this.filteredCollection.map(logistic => {
            const categoryName = this.workflowCategoriesStore.getNameById(
              logistic.formCategoryId,
            )
            const categoryId = logistic.formCategoryId || UNASSIGNED
            return {
              name: categoryName || UNASSIGNED,
              id: categoryId,
              object: this.getCommonGroupingData(currentBand, categoryId),
            }
          }),
          groupingCommonMapper(UNASSIGNED),
        ]
    }
  }

  private getCommonGroupingData = (
    groupingKey: string,
    categoryId: string,
  ): any => {
    return getWorkflowGroupingVisualData({
      groupingKey,
      categoryId,
      permitTypesStore: this.workflowTypesStore,
      formCategoriesStore: this.workflowCategoriesStore,
      companiesStore: this.companiesStore,
      projectMembersStore: this.projectMembersStore,
    })
  }

  @computed
  protected get bandObjectMap(): {
    [bandType: string]: { [groupName: string]: ILogisticItem[] }
  } {
    const map = {}

    const groupingOptions = Object.values(LogisticsGroupingOption)

    groupingOptions.forEach(band => {
      const bandMap = (map[band] = {})

      this.filteredCollection.forEach(logistic => {
        const groupNames: string[] = this.getCategoryIds(logistic, band)
        if (!groupNames.length) {
          groupNames.push(UNASSIGNED)
        }

        groupNames.forEach(groupName => {
          if (!bandMap[groupName]) {
            bandMap[groupName] = []
          }

          bandMap[groupName].push(logistic)
        })
      })

      for (const name in bandMap) {
        bandMap[name].sort((a, b) => a.startDate - b.startDate)
      }
    })

    return map
  }

  protected toRows(logistics: ILogisticItem[]) {
    return logistics.map(logistic => {
      const baseData = {
        [LogisticsDataKeys.ID]: logistic.id,
        [LogisticsDataKeys.CHECKBOX]: this.selection.get(logistic.id),
        [LogisticsDataKeys.LOGISTIC_NAME]: {
          id: logistic.id,
          name: logistic.shortName || logistic.name,
          workflow: isLogisticWorkflow(logistic)
            ? logistic.original
            : undefined,
        },
        [LogisticsDataKeys.LOGISTIC_TYPE]: {
          type: logistic.type,
          workflow: isLogisticWorkflow(logistic)
            ? logistic.original
            : undefined,
        },
        [LogisticsDataKeys.LOGISTIC_CATEGORY]: {
          categoryName:
            this.workflowCategoriesStore?.getNameById(
              logistic.formCategoryId,
            ) || logistic.formCategoryId,
          categoryId: logistic.formCategoryId,
          workflow: isLogisticWorkflow(logistic)
            ? logistic.original
            : undefined,
        },
        [LogisticsDataKeys.LOGISTIC_STATUS]: {
          status: logistic.status,
          workflowStepLevel: logistic.workflowStepLevel,
          isLate: logistic.isStatusLate,
        },
        [LogisticsDataKeys.LOGISTIC_LOCATION]: {
          isBreadcrumb: false,
          locations: logistic.locations,
        },
        [LogisticsDataKeys.LOGISTIC_COMPANY]: logistic.companyIds
          ?.map(id => this.companiesStore.getCompanyById(id))
          .filter(c => c),
        [LogisticsDataKeys.LOGISTIC_DATE]: {
          startDate: logistic?.startDate,
          endDate: logistic?.endDate,
        },
        [LogisticsDataKeys.LOGISTIC_RESPONSIBLE]:
          this.projectMembersStore.getByIds(logistic.responsibleContactIds),
        [LogisticsDataKeys.LOGISTIC_ATTACHMENT]: {
          attachments: logistic.attachments?.map((a, idx) => ({
            ...a,
            id: a.fileName + idx,
            name: a.fileName,
          })),
          [LogisticsDataKeys.LOGISTIC_SHARED_ENTITY]: {
            entity: this.logisticsStore.getSharedFormById(logistic.entityId),
          },
        },
        [LogisticsDataKeys.LOGISTIC_ID]: {
          logisticId: logistic.entityId,
          code: logistic.code,
          isDraft: logistic.status === SitePermitStatus.Draft,
          isShared: logistic.isShared,
        },
        [LogisticsDataKeys.LOGISTIC_EQUIPMENT]: {
          isBreadcrumb: false,
          locations: logistic.equipment,
        },
      }

      return { data: baseData }
    })
  }

  public getSelectedPermits(): Workflow[] {
    const selectedIds = this.selectedInstancesIds || []
    if (!selectedIds.length) return []

    return selectedIds
      .map(id => this.filteredCollection.find(l => l.id === id))
      .filter((l: ILogisticItemWorkflow) => !!l && isLogisticWorkflow(l))
      .map((l: ILogisticItemWorkflow) => l.original as Workflow)
  }

  @computed
  public get getSelectedNonDraftPermits(): Workflow[] {
    return (
      this.getSelectedPermits().filter(
        p => p.status !== SitePermitStatus.Draft,
      ) || []
    )
  }

  public getCommonStepItems(permits: Workflow[]): IStatusItem[] {
    return getCommonStepItems(permits, this.workflowTypesStore)
  }

  protected get instancesInPeriodInterval(): ILogisticItem[] {
    return this.logisticsStore.logisticItemsInPeriodInterval
  }

  protected compareRows = (
    { data: aRowData }: ILWFCRow,
    { data: bRowData }: ILWFCRow,
  ): number => {
    const { columnKey, order } = this.sortState

    const v1 = this.getSortingValue(aRowData, columnKey)
    const v2 = this.getSortingValue(bRowData, columnKey)

    if (typeof v1 === 'string' && typeof v2 === 'string') {
      if (unassignedValues.includes(v1)) {
        return 1
      }
      if (unassignedValues.includes(v2)) {
        return -1
      }
      return this.collator.compare(v1, v2) * order
    }

    if (typeof v1 === 'number' && typeof v2 === 'number') {
      return (v1 - v2) * order
    }

    return 0
  }

  protected searchFiltering = (logistic: ILogisticItem) => {
    const key = this.filter.searchKey.toLowerCase()
    const entityValues: string[] = [
      ...logistic.locations.map(l => l.name),
      logistic.name,
      this.projectDateStore.getWeekdayMonthDayYearAndTimeToDisplay(
        logistic.startDate,
      ),
      this.projectDateStore.getWeekdayMonthDayYearAndTimeToDisplay(
        logistic.endDate,
      ),
      logistic.app,
      logistic.status,
      ...this.projectMembersStore
        .getByIds(logistic.responsibleContactIds)
        .map(u => u.fullName),
      ...logistic.companyIds.map(id =>
        this.companiesStore.getCompanyNameById(id),
      ),
      ...(logistic.attachments?.map(a => a.fileName) || []),
    ]
    return entityValues.some(value => value?.toLowerCase().includes(key))
  }

  protected createCategoryRow(categoryId: string, count: number): ILWFCRow {
    const categoryInstances = this.categoryToInstancesMap[categoryId]

    const category: ILWFCCategory = {
      categoryId,
      shortCategoryLabel: this.getCategoryLabelById(categoryId),
      categoryLabel: `${this.getCategoryLabelById(categoryId)} (${count})`,
      isChecked: categoryInstances.every(
        inst =>
          !this.canSelectInstance(inst) || this.selection.get(inst[this.idKey]),
      ),
    }

    return { category, data: {}, height: CATEGORY_ROW_HEIGHT, level: 0 }
  }

  protected createTreeNodeCategoryRow(
    categoryId: string,
    categoryName: string,
    categoryObject: any,
    level: number,
    objects: ILogisticItem[],
  ): ILWFCRow {
    const countLabel = this.shouldShowInstanceCount
      ? ` (${objects.length})`
      : ''
    const label = categoryName + countLabel

    const category: ILWFCCategory = {
      categoryId,
      shortCategoryLabel: categoryName,
      countLabel: countLabel,
      categoryLabel: label,
      isChecked: objects.every(
        inst =>
          !this.canSelectInstance(inst) || this.selection.get(inst[this.idKey]),
      ),
      idsToSelect: objects.map(d => d[this.idKey]),
    }

    return {
      category,
      data: categoryObject,
      height: CATEGORY_ROW_HEIGHT,
      level,
    }
  }

  protected getCategoryLabelById(categoryId: any): string {
    switch (this.groupingKey) {
      case LogisticsGroupingOption.RESPONSIBLE:
        const user = this.projectMembersStore.getById(categoryId)
        return User.getFullNameToDisplay(user, this.userProjectsStore, true)
      default:
        return categoryId
    }
  }

  protected getSortingValue(rowData: LWFCRowData, columnKey: string) {
    switch (columnKey) {
      case LogisticsDataKeys.LOGISTIC_ID:
        return rowData[columnKey]?.code || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_NAME:
        return rowData[columnKey]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_TYPE:
        return rowData[columnKey]?.type || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_CATEGORY:
        return rowData[columnKey]?.categoryName || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_STATUS:
        return rowData[columnKey]?.status || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_LOCATION:
        const locations = rowData[columnKey]?.locations
        return locations?.length > 1
          ? locations.length.toString()
          : locations?.[0]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_EQUIPMENT:
        const equipment = rowData[columnKey]?.locations
        return equipment?.length > 1
          ? equipment.length.toString()
          : equipment?.[0]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_COMPANY:
        const companiesData = rowData[columnKey]
        return companiesData?.length > 1
          ? companiesData.length.toString()
          : companiesData?.[0]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_DATE:
        return rowData[columnKey]?.startDate || 0
      case LogisticsDataKeys.LOGISTIC_RESPONSIBLE:
        const usersData = rowData[columnKey]
        return usersData?.length > 1
          ? usersData.length.toString()
          : usersData?.[0]?.fullName || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_ATTACHMENT:
        const attachments = rowData[columnKey]?.attachments
        return attachments?.length > 1
          ? attachments.length.toString()
          : attachments?.[0]?.fileName || EMPTY_STRING
      default:
        const value = rowData[columnKey]
        return value === NO_SPECIFIED ? EMPTY_STRING : value
    }
  }

  private getLocationCategoryIds = (locations: LocationBase[]): string[] => {
    return Array.from(
      locations?.reduce(
        (set, l) => set.add(`${l.id}${LOCATION_SEPARATOR}${l.type}`),
        new Set<string>(),
      ),
    )
  }

  @action.bound
  public batchUpdateStatuses(item: IStatusItem, eventsStore: EventsStore) {
    if (!this.selectedInstancesIds.length) return

    const permits = this.getSelectedNonDraftPermits

    const toSave: Workflow[] = []
    permits.forEach(permit => {
      const permitCopy = permit.copy()
      permitCopy.setStatus(item.status)

      const permitType = this.workflowTypesStore.getPermitTypeById(
        permit.typeId,
      )
      if (permitType) {
        const step = resolveStepForStatus(
          item.status,
          item.workflowStepLevel,
          permitType,
        )
        if (step) {
          permitCopy.setWorkflowStepId(step.id)
          if (
            item.status !== SitePermitStatus.Denied &&
            item.status !== SitePermitStatus.Failed
          ) {
            advanceToNextWorkflowStep(
              permitCopy,
              permitType.stepsWithoutRecurring,
            )
          } else {
            const steps = permitType.stepsWithoutRecurring
            const isLastStep = step.id === steps[steps.length - 1]?.id
            permitCopy.setWorkflowStepLevel(
              isLastStep ? 0 : getWorkflowStepLevel(step.id, steps),
            )
          }
        }
      }

      toSave.push(permitCopy)
    })

    if (toSave.length) {
      eventsStore.dispatch(e.SAVE_WORKFLOWS, toSave)
    }
  }
}
